import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Eye, Filter } from "lucide-react";
import { listAllQuotes } from "../../reducers/quote.slice";
import { convertDateFormatInUtc, formatNumberToDollar, countActiveFiltersAndSort } from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import DataTable from "../../components/shared/DataTable";
import PageHeader from "../../components/shared/PageHeader";
import RefreshButton from "../../components/shared/RefreshButton";
import SearchBar from "../../components/shared/SearchBar";
import StatusBadge from "../../components/shared/StatusBadge";
import useMediaQuery from "../../components/shared/useMediaQuery";
import PendingQuoteDetailContent, { statusLabel } from "./PendingQuoteDetailContent";

const QUOTE_STATUSES = ["QUOTE_PENDING", "QUOTE_APPROVED", "QUOTE_REJECTED", "QUOTE_ACCEPTED", "QUOTE_DENIED"];
const ROWS_PER_PAGE_DEFAULT = 5;

const PendingRSCQuotesPage = () => {
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [allQuotes, setAllQuotes] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const s = sessionStorage.getItem("pendingRscQuotes_selectedStatuses");
    return s ? JSON.parse(s) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem("pendingRscQuotes_sortDirection") || null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_DEFAULT);
  const [loading, setLoading] = useState(false);
  const [detailQuote, setDetailQuote] = useState(null);

  const getAllPRSCQuotes = () => {
    setLoading(true);
    dispatch(listAllQuotes()).then((res) => {
      if (res?.error && Object.keys(res.error).length > 0) {
        setLoading(false);
        showToast(res.error.message, "error");
        return;
      }
      const formatted = res?.payload?.map((item) => ({
        ...item,
        createdDate: convertDateFormatInUtc(item.created_at, "MMM D, YYYY"),
        cashoutPrice: formatNumberToDollar(item.cashout_price),
        totalAmount: formatNumberToDollar(item.total_amount),
      }));
      setAllQuotes(formatted || []);
      setLoading(false);
    });
  };

  useEffect(() => {
    getAllPRSCQuotes();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    let result = [...allQuotes];
    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (item) =>
          item.item_name?.toLowerCase().includes(q) ||
          item.upc?.toLowerCase().includes(q) ||
          item.id?.toLowerCase().includes(q)
      );
    }
    if (selectedStatuses.length > 0) {
      result = result.filter((item) => selectedStatuses.includes(item.status));
    }
    if (sortDirection === "asc") {
      result.sort((a, b) => (parseFloat(a.total_amount) || 0) - (parseFloat(b.total_amount) || 0));
    } else if (sortDirection === "desc") {
      result.sort((a, b) => (parseFloat(b.total_amount) || 0) - (parseFloat(a.total_amount) || 0));
    }
    setFilteredData(result);
    setCurrentPage(0);
  }, [allQuotes, debouncedSearch, selectedStatuses, sortDirection]);

  useEffect(() => {
    sessionStorage.setItem("pendingRscQuotes_selectedStatuses", JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    if (sortDirection) sessionStorage.setItem("pendingRscQuotes_sortDirection", sortDirection);
    else sessionStorage.removeItem("pendingRscQuotes_sortDirection");
  }, [sortDirection]);

  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => (checked ? [...prev, status] : prev.filter((s) => s !== status)));
  };

  const quoteStats = useMemo(() => {
    const pending = allQuotes.filter((q) => q.status === "QUOTE_PENDING");
    const approved = allQuotes.filter((q) => q.status === "QUOTE_APPROVED");
    const rejected = allQuotes.filter((q) => q.status === "QUOTE_REJECTED" || q.status === "QUOTE_DENIED");
    return { pending: pending.length, approved: approved.length, rejected: rejected.length };
  }, [allQuotes]);

  const columns = [
    {
      key: "item_name",
      label: "Item Name",
      width: "28%",
      maxWidth: 260,
      render: (row) => (
        <a
          href={row.item_link || "#"}
          target="_blank"
          rel="noopener noreferrer"
          title={row.item_name}
          style={{ color: "#4fb8f0", textDecoration: "underline", fontSize: 12.5 }}
          onClick={(e) => {
            e.stopPropagation();
            if (!row.item_link) {
              showToast("Item Link not available");
              e.preventDefault();
            }
          }}
        >
          {row.item_name}
        </a>
      ),
    },
    { key: "createdDate", label: "Order Date", width: "13%" },
    { key: "upc", label: "UPC", width: "13%" },
    { key: "quantity", label: "Quantity", width: "9%", align: "right" },
    { key: "cashoutPrice", label: "Cash Out Price", width: "13%", align: "right" },
    { key: "totalAmount", label: "Total Amount", width: "13%", align: "right" },
    {
      key: "status",
      label: "Status",
      width: "11%",
      align: "center",
      render: (row) => <StatusBadge status={row.status} label={statusLabel(row.status)} />,
    },
    {
      key: "action",
      label: "Action",
      width: "8%",
      align: "center",
      render: (row) => (
        <button
          type="button"
          aria-label="view pending quote"
          onClick={(e) => {
            e.stopPropagation();
            setDetailQuote(row);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 30,
            height: 30,
            borderRadius: 8,
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#c3c8cc",
            cursor: "pointer",
          }}
        >
          <Eye size={14} />
        </button>
      ),
    },
  ];

  const paginatedRows = filteredData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader
          title="Pending RSC Quotes"
          subtitle="Review and approve pending quotes by entering the appropriate prices for each item."
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "145px" : "180px"}, 1fr))`,
          gap: isMobile ? 12 : 16,
          marginBottom: 20,
        }}
      >
        <div style={statStyle("#e8b84b")}>
          <span style={statLabelStyle}>PENDING</span>
          <span style={statValueStyle}>{quoteStats.pending}</span>
        </div>
        <div style={statStyle("#4caf50")}>
          <span style={statLabelStyle}>APPROVED</span>
          <span style={statValueStyle}>{quoteStats.approved}</span>
        </div>
        <div style={statStyle("#f27059")}>
          <span style={statLabelStyle}>REJECTED / DENIED</span>
          <span style={statValueStyle}>{quoteStats.rejected}</span>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? 10 : 12,
            flexWrap: "wrap",
            width: isMobile ? "100%" : "auto",
          }}
        >
          {!isMobile && <RefreshButton onRefresh={getAllPRSCQuotes} spinning={loading} />}
          <div style={{ flex: isMobile ? 1 : "none", minWidth: isMobile ? 0 : undefined }}>
            <SearchBar
              placeholder="Search quotes..."
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              setDebouncedTerm={setDebouncedSearch}
            />
          </div>
          <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="default" style={{ width: isMobile ? "100%" : "auto" }}>
                <Filter size={14} />
                Filter & Sort
                {activeFilterCount > 0 && (
                  <span
                    style={{
                      marginLeft: 4,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 16,
                      height: 16,
                      borderRadius: 8,
                      background: "#00ADB5",
                      color: "#000",
                      fontSize: 10,
                      fontWeight: 800,
                      padding: "0 4px",
                    }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{ minWidth: 240 }}>
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              {QUOTE_STATUSES.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={selectedStatuses.includes(status)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) => handleStatusChange(status, checked)}
                >
                  {statusLabel(status)}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem active={sortDirection === "asc"} onClick={() => setSortDirection((prev) => prev === "asc" ? null : "asc")}>
                <ArrowUpNarrowWide size={14} style={{ marginRight: 8 }} />
                Sort by Amount (Low to High)
              </DropdownMenuItem>
              <DropdownMenuItem active={sortDirection === "desc"} onClick={() => setSortDirection((prev) => prev === "desc" ? null : "desc")}>
                <ArrowDownWideNarrow size={14} style={{ marginRight: 8 }} />
                Sort by Amount (High to Low)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={paginatedRows}
        totalRows={filteredData.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(n) => setCurrentPage(n)}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setCurrentPage(0);
        }}
        loading={loading}
        minWidth={980}
      />

      <Dialog open={Boolean(detailQuote)} onOpenChange={(open) => !open && setDetailQuote(null)}>
        <DialogContent
          style={{
            maxWidth: 1040,
            width: isMobile ? "calc(100vw - 16px)" : "min(1040px, calc(100vw - 64px))",
            background: "#111",
          }}
        >
          <DialogHeader>
            <DialogTitle>Pending RSC Quote</DialogTitle>
            <DialogDescription>Review quote details, pricing, expiry, and decision history.</DialogDescription>
          </DialogHeader>
          {detailQuote && (
            <PendingQuoteDetailContent
              quote={detailQuote}
              dialogMode
              onUpdated={() => {
                getAllPRSCQuotes();
                setDetailQuote(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const statStyle = (accent) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: 14,
  padding: "16px 18px",
});

const statLabelStyle = {
  display: "block",
  color: "#c3c8cc",
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.09em",
};

const statValueStyle = {
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: 24,
  fontWeight: 800,
  marginTop: 8,
};

export default PendingRSCQuotesPage;
